/**
 * EntityAutocompleteField Component (issue #769)
 *
 * Generic filterable/typeahead entity selector built on Tyler Forge's
 * `forge-autocomplete`. Extracted from `TeamAutocompleteField` (issue #649),
 * which is now a thin, Team-flavored wrapper around this component —
 * `forge-autocomplete` has no content slots, so the option list is supplied
 * via an imperative `.filter` callback and the selection is reported through
 * the `forge-autocomplete-change` custom event, both wired through a ref.
 *
 * Use this directly for any "pick one entity by name from a catalog" field
 * (e.g. a Business Process's Process Owner or Owning Business Unit); reach
 * for a dedicated wrapper only when a field needs baked-in defaults specific
 * to one entity type (as `TeamAutocompleteField` does for Team).
 */

import { useCallback, useEffect, useRef } from 'react';
import { type FieldError } from 'react-hook-form';
import { ForgeAutocomplete } from '@tylertech/forge-react';
import type { IAutocompleteComponent, IAutocompleteOption } from '@tylertech/forge';
import '@tylertech/forge/autocomplete';
import '@tylertech/forge/text-field';
import './EntityAutocompleteField.css';

export interface EntityAutocompleteOption {
  /** Entity id */
  value: string;
  /** Entity display name */
  label: string;
}

export interface EntityAutocompleteFieldProps {
  /** Field name — used for the input id/name and error association */
  name: string;
  /** Field label */
  label?: string;
  /** Entities the user may select */
  options: EntityAutocompleteOption[];
  /** Currently selected entity id */
  value?: string;
  /** Called with the selected entity's id, or '' when cleared */
  onChange: (entityId: string) => void;
  /** Blur handler */
  onBlur?: () => void;
  /** Placeholder text */
  placeholder?: string;
  /** Is field required */
  required?: boolean;
  /** Is field disabled */
  disabled?: boolean;
  /** Helper text */
  helperText?: string;
  /** Validation error from React Hook Form */
  error?: FieldError;
  /** Message shown when the filter text matches no entity */
  emptyMessage?: string;
  /** Wrapper `<div>` class name — override to scope entity-specific styling */
  wrapperClassName?: string;
}

const ForgeTextField = 'forge-text-field' as React.ElementType;

/**
 * Filterable entity dropdown adapter for React Hook Form.
 *
 * Unlike `SelectField`, this does not forward `register()`'s `onChange`
 * directly — `forge-autocomplete` reports selection via a custom
 * `forge-autocomplete-change` event rather than a native `change` event, so
 * the parent must call `setValue(fieldName, entityId)` from
 * `onChange(entityId)`.
 *
 * @example
 * ```tsx
 * <EntityAutocompleteField
 *   name="processOwnerId"
 *   label="Process Owner"
 *   options={stakeholders.map((s) => ({ value: s.entityId, label: s.name }))}
 *   value={values.processOwnerId}
 *   onChange={(id) => setValue('processOwnerId', id, { shouldValidate: true })}
 *   error={errors.processOwnerId}
 * />
 * ```
 */
export function EntityAutocompleteField({
  name,
  label,
  options,
  value,
  onChange,
  onBlur,
  placeholder = 'Search…',
  required = false,
  disabled = false,
  helperText,
  error,
  emptyMessage = 'No matches found',
  wrapperClassName = 'entity-autocomplete-field-wrapper',
}: EntityAutocompleteFieldProps): JSX.Element {
  const autocompleteRef = useRef<IAutocompleteComponent | null>(null);

  // `.filter` is an imperative property set once — it always reads the
  // latest options through a ref rather than closing over a stale list, so
  // every *new* filter() call (typing, or filterOnFocus re-opening the field)
  // sees current data. What this does NOT do: proactively re-render a
  // dropdown that's already open and showing a stale result (e.g. opened
  // while the family/tag query is still in flight) — Forge only re-invokes
  // filter() on the next input/focus event, not on options changing
  // underneath it. Self-corrects on the next keystroke or refocus; flagged
  // and accepted as a launch-timing edge case in review (PR #1874, issue
  // #1865), not a functional bug.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    const el = autocompleteRef.current;
    if (!el) return;
    el.filter = (filterText: string): Promise<IAutocompleteOption[]> => {
      const query = filterText.trim().toLowerCase();
      const matches = query
        ? optionsRef.current.filter((option) => option.label.toLowerCase().includes(query))
        : optionsRef.current;
      return Promise.resolve(matches);
    };
  }, []);

  const handleChange = useCallback(
    (event: Event): void => {
      const detail = (event as CustomEvent<string | EntityAutocompleteOption | null | undefined>)
        .detail;
      if (detail == null) {
        onChange('');
        return;
      }
      onChange(typeof detail === 'string' ? detail : detail.value);
    },
    [onChange]
  );

  useEffect(() => {
    const el = autocompleteRef.current;
    if (!el) return;
    el.addEventListener('forge-autocomplete-change', handleChange);
    return () => el.removeEventListener('forge-autocomplete-change', handleChange);
  }, [handleChange]);

  // Keep the component's internal selection synced with the controlled
  // `value` prop (edit-mode default load, or a reset from the parent form).
  //
  // Deliberately no `matchKey` prop on <ForgeAutocomplete>: we always assign
  // a full { value, label } object here, so Forge never needs to resolve a
  // bare primitive against the option list. Setting `matchKey` triggers a
  // real bug in Forge's internal `optionEqualPredicate` — on selecting an
  // option, its `_onSelect` handler compares the clicked list row's raw
  // internal value (a bare string) against each option using
  // `matchKey in value`, which throws
  // "Cannot use 'in' operator to search for '<matchKey>' in <string>"
  // because `in` requires an object on the right-hand side. Confirmed via a
  // CI trace (issue #649) — do not re-add `matchKey` here.
  useEffect(() => {
    const el = autocompleteRef.current;
    if (!el) return;
    if (!value) {
      el.value = null;
      return;
    }
    // If `options` hasn't loaded yet (e.g. a deep-linked filter value from
    // the URL resolving before its entity query returns), this falls back to
    // showing the bare id as its own label. Accepted as a brief flicker,
    // self-correcting once the options list arrives and this effect re-runs
    // — flagged and confirmed acceptable in review (PR #1874, issue #1865).
    const match = optionsRef.current.find((option) => option.value === value);
    el.value = match ?? { value, label: value };
  }, [value, options]);

  const hasError = Boolean(error);
  const errorId = hasError ? `${name}-error` : undefined;

  // Screen-reader parity for the "nothing selected" state (issue #1878).
  //
  // The `<select>`/`<option value="">All X</option>` row this component
  // replaced always had an accessible *value* — its chosen option's text —
  // that a screen reader announces on focus. A bare `placeholder` on this
  // text input is not a substitute: browsers expose it to the accessibility
  // tree inconsistently (some map it to the accessible *name*, some don't
  // expose it at all), so "no filter selected" wasn't reliably announced.
  //
  // Fix: mirror the placeholder text into a visually-hidden node and wire it
  // up via `aria-describedby`, which every screen reader reads
  // deterministically. Only wired while the field is genuinely empty — once
  // a value is selected, the input's own text *is* the accessible value
  // (exactly like a `<select>` announcing its now-chosen option), so the
  // "nothing selected" description would be redundant noise on every focus.
  const isEmpty = !value;
  const emptyStateId = isEmpty ? `${name}-empty-state` : undefined;
  const describedBy = [errorId, emptyStateId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={wrapperClassName}>
      <ForgeAutocomplete
        ref={autocompleteRef as React.Ref<unknown>}
        filterOnFocus
        emptyMessage={emptyMessage}
      >
        <ForgeTextField invalid={hasError || undefined} disabled={disabled || undefined}>
          {label && (
            <label htmlFor={name} className="form-label">
              {label}
              {required && (
                <span className="form-required" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          )}
          {isEmpty && (
            <span id={emptyStateId} className="entity-autocomplete-empty-state-description">
              {placeholder}
            </span>
          )}
          <input
            id={name}
            name={name}
            type="text"
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            onBlur={onBlur}
            aria-required={required || undefined}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
          />
          {hasError && error?.message && (
            <div id={errorId} slot="support-text" className="form-error-message" role="alert">
              {error.message}
            </div>
          )}
          {!hasError && helperText && (
            <div slot="support-text" className="form-helper-text">
              {helperText}
            </div>
          )}
        </ForgeTextField>
      </ForgeAutocomplete>
    </div>
  );
}
